import { TaskCategoryItem, TaskItem, TaskStatusDefinition } from '../types/Task';
import { formatCategoryLabel } from './taskCategoryUtils';
import { getTaskStatusLabel } from './taskStatusTransitions';

export type ChartGroupField = 'assignee' | 'status' | 'priority' | 'category';
export type ChartView = 'bar' | 'pie' | 'table';
export type ChartRow = { label: string; count: number };
export type PieChartSegment = ChartRow & {
  color: string;
  percentage: number;
  dash: string;
  dashOffset: number;
};

const chartPalette = ['#2e6fd8', '#23a3a1', '#7c5cff', '#d97706', '#dc2626', '#4f46e5', '#0f766e', '#65a30d', '#be185d', '#0891b2'];

const escapeSvgText = (value: string) => value
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

export const sanitizeChartFileName = (value: string) => value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'chart';

export const triggerBlobDownload = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export const svgMarkupToPngBlob = async (svgMarkup: string, width: number, height: number) => {
  const blob = new Blob([svgMarkup], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);

  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const nextImage = new Image();
      nextImage.onload = () => resolve(nextImage);
      nextImage.onerror = () => reject(new Error('Failed to render chart image.'));
      nextImage.src = url;
    });

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Canvas export is not supported in this browser.');
    }

    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, width, height);
    context.drawImage(image, 0, 0, width, height);

    const pngBlob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(result => {
        if (result) {
          resolve(result);
          return;
        }
        reject(new Error('Failed to generate PNG file.'));
      }, 'image/png');
    });

    return pngBlob;
  } finally {
    URL.revokeObjectURL(url);
  }
};

export const buildChartRows = (
  tasks: TaskItem[],
  chartGroupField: ChartGroupField,
  employeeLookup: Record<string, { name: string; profileImageUrl?: string; colorPreference?: string }>,
  statusOptions: TaskStatusDefinition[],
  t: (key: string, options?: Record<string, unknown>) => string,
  categories: TaskCategoryItem[] = []
): ChartRow[] => {
  const counts = new Map<string, number>();
  const bump = (label: string) => counts.set(label, (counts.get(label) || 0) + 1);

  tasks.forEach(task => {
    if (chartGroupField === 'assignee') {
      if (!task.assignedToUserId) {
        bump(t('unassigned', { ns: 'tasks' }));
        return;
      }
      bump(employeeLookup[task.assignedToUserId]?.name || t('unknownAssignee', { ns: 'tasks' }));
      return;
    }

    if (chartGroupField === 'status') {
      bump(getTaskStatusLabel(task.status, statusOptions));
      return;
    }

    if (chartGroupField === 'priority') {
      bump(t(`priority.${task.priority}`, { ns: 'tasks' }));
      return;
    }

    bump(task.category ? formatCategoryLabel(task.category, categories) : t('none', { ns: 'tasks' }));
  });

  return Array.from(counts.entries())
    .map(([label, count]) => ({ label, count }))
    .sort((left, right) => right.count - left.count || left.label.localeCompare(right.label));
};

export const buildPieChartSegments = (chartRows: ChartRow[], totalChartCount: number): PieChartSegment[] => {
  if (totalChartCount <= 0) {
    return [];
  }

  const circumference = 2 * Math.PI * 42;
  let offset = 0;

  return chartRows.map((item, index) => {
    const percentage = item.count / totalChartCount;
    const arc = circumference * percentage;
    const segment = {
      ...item,
      percentage,
      color: chartPalette[index % chartPalette.length],
      dash: `${arc} ${circumference - arc}`,
      dashOffset: -offset,
    };
    offset += arc;
    return segment;
  });
};

export const buildBarChartSvg = (rows: ChartRow[], maxCount: number, title: string) => {
  const width = 1200;
  const rowHeight = 54;
  const top = 110;
  const left = 280;
  const chartWidth = 820;
  const height = top + rows.length * rowHeight + 80;

  const bars = rows.map((row, index) => {
    const y = top + index * rowHeight;
    const barWidth = maxCount > 0 ? Math.max(18, (row.count / maxCount) * chartWidth) : 18;
    return `
      <text x="${left - 20}" y="${y + 23}" text-anchor="end" font-size="20" fill="#0f172a" font-family="Segoe UI, Arial, sans-serif">${escapeSvgText(row.label)}</text>
      <rect x="${left}" y="${y}" width="${chartWidth}" height="28" rx="8" fill="#e8eef7" />
      <rect x="${left}" y="${y}" width="${barWidth}" height="28" rx="8" fill="#2e6fd8" />
      <text x="${left + chartWidth + 16}" y="${y + 23}" font-size="18" fill="#334155" font-family="Segoe UI, Arial, sans-serif" font-weight="700">${row.count}</text>
    `;
  }).join('');

  return {
    width,
    height,
    markup: `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
      <rect width="100%" height="100%" fill="#ffffff" />
      <text x="60" y="58" font-size="34" fill="#0f172a" font-family="Segoe UI, Arial, sans-serif" font-weight="700">${escapeSvgText(title)}</text>
      <text x="60" y="88" font-size="18" fill="#64748b" font-family="Segoe UI, Arial, sans-serif">Horizontal bar chart</text>
      ${bars}
    </svg>`,
  };
};

export const buildPieChartSvg = (rows: PieChartSegment[], total: number, title: string) => {
  const width = 1200;
  const height = 760;
  const cx = 260;
  const cy = 380;
  const radius = 150;
  const legendX = 540;
  const legendY = 180;

  const segments = rows.map(row => `
    <circle cx="${cx}" cy="${cy}" r="${radius}" fill="none" stroke="${row.color}" stroke-width="52" stroke-dasharray="${row.dash}" stroke-dashoffset="${row.dashOffset}" transform="rotate(-90 ${cx} ${cy})" />
  `).join('');

  const legend = rows.map((row, index) => `
    <circle cx="${legendX}" cy="${legendY + index * 46}" r="8" fill="${row.color}" />
    <text x="${legendX + 20}" y="${legendY + index * 46 + 6}" font-size="20" fill="#0f172a" font-family="Segoe UI, Arial, sans-serif">${escapeSvgText(row.label)}</text>
    <text x="1060" y="${legendY + index * 46 + 6}" text-anchor="end" font-size="18" fill="#475569" font-family="Segoe UI, Arial, sans-serif">${Math.round(row.percentage * 100)}%</text>
    <text x="1120" y="${legendY + index * 46 + 6}" text-anchor="end" font-size="18" fill="#334155" font-family="Segoe UI, Arial, sans-serif" font-weight="700">${row.count}</text>
  `).join('');

  return {
    width,
    height,
    markup: `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
      <rect width="100%" height="100%" fill="#ffffff" />
      <text x="60" y="58" font-size="34" fill="#0f172a" font-family="Segoe UI, Arial, sans-serif" font-weight="700">${escapeSvgText(title)}</text>
      <text x="60" y="88" font-size="18" fill="#64748b" font-family="Segoe UI, Arial, sans-serif">Donut chart</text>
      <circle cx="${cx}" cy="${cy}" r="${radius}" fill="none" stroke="#e6edf7" stroke-width="52" />
      ${segments}
      <text x="${cx}" y="${cy - 6}" text-anchor="middle" font-size="44" fill="#0f172a" font-family="Segoe UI, Arial, sans-serif" font-weight="700">${total}</text>
      <text x="${cx}" y="${cy + 28}" text-anchor="middle" font-size="20" fill="#64748b" font-family="Segoe UI, Arial, sans-serif">items</text>
      ${legend}
    </svg>`,
  };
};

export const buildTableChartSvg = (rows: ChartRow[], total: number, title: string) => {
  const width = 1200;
  const rowHeight = 44;
  const headerY = 140;
  const height = headerY + (rows.length + 1) * rowHeight + 40;
  const renderRow = (label: string, count: string, share: string, index: number, header = false) => {
    const y = headerY + index * rowHeight;
    const fill = header ? '#f8fbff' : '#ffffff';
    const fontWeight = header ? 700 : 400;
    return `
      <rect x="60" y="${y}" width="1080" height="${rowHeight}" fill="${fill}" stroke="#e6edf7" />
      <text x="84" y="${y + 28}" font-size="18" fill="#0f172a" font-family="Segoe UI, Arial, sans-serif" font-weight="${fontWeight}">${escapeSvgText(label)}</text>
      <text x="930" y="${y + 28}" text-anchor="end" font-size="18" fill="#334155" font-family="Segoe UI, Arial, sans-serif" font-weight="${fontWeight}">${count}</text>
      <text x="1100" y="${y + 28}" text-anchor="end" font-size="18" fill="#334155" font-family="Segoe UI, Arial, sans-serif" font-weight="${fontWeight}">${share}</text>
    `;
  };

  const rowsMarkup = rows.map((row, index) => renderRow(row.label, String(row.count), total > 0 ? `${Math.round((row.count / total) * 100)}%` : '0%', index + 1)).join('');

  return {
    width,
    height,
    markup: `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
      <rect width="100%" height="100%" fill="#ffffff" />
      <text x="60" y="58" font-size="34" fill="#0f172a" font-family="Segoe UI, Arial, sans-serif" font-weight="700">${escapeSvgText(title)}</text>
      <text x="60" y="88" font-size="18" fill="#64748b" font-family="Segoe UI, Arial, sans-serif">Table view</text>
      ${renderRow('Value', 'Count', 'Share', 0, true)}
      ${rowsMarkup}
    </svg>`,
  };
};
